import {
    getHomepageStoredTab,
} from '~spa/Components/Homepage/utils';
import {
    HOMEPAGE_DEFAULT_TAB,
    HOMEPAGE_LOCATION,
} from '~spa/Pages/Homepage/constants';
/**
 * GA_COMPONENT_CSS_ATTRIBUTE_NAME is also used for betLocate reporting.
 * Some code imports this directly. Use of wrapper functions is preferred
 */
export const GA_COMPONENT_CSS_ATTRIBUTE_NAME = 'data-ga_component';
export const GA_COMPONENT_UNKNOWN_COMPONENT_VALUE = 'unknown_component';

/**
 * Adds GA component to sport - Only required for vanilla JS components
 * @param {Element} element the element to add ga to
 * @param {String} componentName the GA component name
 * @return {void}
 */
export function setAsGaComponent(element, componentName) {
    element.setAttribute(GA_COMPONENT_CSS_ATTRIBUTE_NAME, `${componentName}`);
}

/**
 * Create a google-analytics/betLocate object
 * Spread it in the HTML element element that wraps your React component
 * Use for generic pages (e.g. homepage, my-funky-page) otherwise use more epecific functions below
 * @param {String} value the value for the attribute
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObject(value) {
    return { [GA_COMPONENT_CSS_ATTRIBUTE_NAME]: value };
}

/**
 * Stuff that looks after themselves but still call getGaAttributeObject() to form the attribute:
 *  - MadeForYou
 *  - ModalWindow (gets pass a value)
 *  - WidgetContainer (gets pass a value)
 *  - BetFinder Widget
 * Ideally the constant is here and there is a function that calls getGaAttributeObject() as for
 * the constants below
 */
const GA_PREFIX_EVENT_PAGE = 'event_page';
const GA_PREFIX_SPORT_PAGE = 'sport_page';
const GA_PREFIX_BETSLIP = 'betslip';
const GA_INPLAY = 'inplay';
const GA_WIDGET = 'widget';
const GA_PREFIX_HOMEPAGE = 'homepage';
const GA_RECOMMENDATIONS = 'recommendations';
const GA_SPOTLIGHT = 'spotlight';

/**
 * Gets the homepage tab key for GA, falling back to the default homepage tab.
 * @returns {String} The homepage tab key used in GA attributes.
 */
const getHomepageGaTabKey = () => getHomepageStoredTab() || HOMEPAGE_DEFAULT_TAB;

/**
 * Gets a GA attribute value for a section within a given bet location.
 * Homepage values include the saved tab key, e.g. homepage_tab-1_inplay.
 * Other locations use the format {betLocate}_{suffix}, e.g. featured_inplay.
 * @param {String} betLocate The location of the bet, e.g. homepage, football, featured.
 * @param {String} suffix The trailing GA section identifier, e.g. inplay or spotlight.
 * @returns {String} GA attribute value.
 */
export function getGaAttributeValue(betLocate, suffix) {
    const tabKeySegment = betLocate === HOMEPAGE_LOCATION ? `_${getHomepageGaTabKey()}` : '';

    return `${betLocate}${tabKeySegment}_${suffix}`;
}

/**
 * Gets a VALUE for inplay section, e.g. schedule_inplay, football_inplay
 * or a homepage section, e.g. homepage_tab-1_inplay
 * @param {String} betLocate The location of the bet, e.g. homepage, sport page etc.
 * @returns {String} GA attribute value
 */
export function getGaAttributeValueInplay(betLocate) {

    return getGaAttributeValue(betLocate, GA_INPLAY);
}

/**
 * Gets a VALUE for a sport page section, e.g. sport_page_football_betfeed, sport_page_golf
 * @param {String} sportSlug the slug
 * @param {String} suffix optional suffix, e.g. used on GenericV2 pages
 * @returns {String} GA attribute value
 */
export function getGaAttributeValueSportPage(sportSlug, suffix = '') {
    const extraSuffix = suffix !== '' ? `_${suffix}` : '';

    return `${GA_PREFIX_SPORT_PAGE}_${sportSlug}${extraSuffix}`;
}

/**
 * Create a google-analytics/betLocate object for sport page section
 * Spread it in the HTML element element that wraps your React component
 * @param {String} sportSlug the slug
 * @param {String} suffix optional suffix, e.g. used on GenericV2 pages
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectSportPage(sportSlug, suffix = '') {
    return getGaAttributeObject(getGaAttributeValueSportPage(sportSlug, suffix));
}

/**
 * Gets a VALUE for a homepage widget section, e.g. homepage_tab-1_horse-racing_widget, homepage_tab-1_betfeed_widget
 * @param {String} slug the slug
 * @returns {String} GA attribute value
 */
export function getGaAttributeValueHomepageWidget(slug) {
    const savedTabKey = getHomepageGaTabKey();

    return `${GA_PREFIX_HOMEPAGE}_${savedTabKey}_${slug}_${GA_WIDGET}`;
}

/**
 * Create a google-analytics/betLocate object for a homepage widget section
 * Spread it in the HTML element element that wraps your React component
 * @param {String} slug the slug
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectHomepageWidget(slug) {
    return getGaAttributeObject(getGaAttributeValueHomepageWidget(slug));
}

/**
 * Gets a VALUE for an event page section, e.g. event_page_golf, event_page_football_specials
 * @param {String} sportSlug the slug
 * @param {String} suffix optional suffix, e.g. used on GenericV2 pages
 * @returns {String} GA attribute value
 */
export function getGaAttributeValueEventPage(sportSlug, suffix = '') {
    const extraSuffix = suffix !== '' ? `_${suffix}` : '';

    return `${GA_PREFIX_EVENT_PAGE}_${sportSlug}${extraSuffix}`;
}

/**
 * Create a google-analytics/betLocate object for an event page section
 * Spread it in the HTML element element that wraps your React component
 * @param {String} sportSlug the slug
 * @param {String} suffix optional suffix, e.g. used on GenericV2 pages
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectEventPage(sportSlug, suffix = '') {
    return getGaAttributeObject(
        getGaAttributeValueEventPage(sportSlug, suffix)
    );
}

/**
 * Create a google-analytics/betLocate object for an INPLAY event page section
 * Spread it in the HTML element element that wraps your React component
 * @param {String} sportSlug the slug
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectInplayEventPage(sportSlug) {
    return getGaAttributeObject(`${GA_PREFIX_EVENT_PAGE}_${GA_INPLAY}_${sportSlug}`);
}

/**
 * Create a google-analytics/betLocate object for a widget page, e.g. betfeed_widget, mfy_widget.
 * These tend to be widgets used as pages, rather than embedded on pages
 * Spread it in the HTML element element that wraps your React component
 * @param {String} slug the slug
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectWidgetPage(slug) {
    return getGaAttributeObject(`${slug}_${GA_WIDGET}`);
}

/**
 * Create a google-analytics/betLocate object for a betslip recommendations section
 * Spread it in the HTML element element that wraps your React component
 * @param {String} displayArea the betslip area (e.g. betslip, betreceipt)
 * @returns {Object} { attributeName: value }
 */
export function getGaAttributeObjectRecommendations(displayArea) {
    return getGaAttributeObject(`${GA_RECOMMENDATIONS}_${displayArea}`);
}

/**
 * Gets a VALUE for an event page section, e.g. whatever_spotlight
 * or a homepage widget section, e.g. homepage_tab-1_spotlight
 * @param {String} betLocate The location of the bet, e.g. homepage, sport page etc.
 * @returns {String} the attribute value
 */
export function getGaAttributeValueSpotlight(betLocate) {

    return getGaAttributeValue(betLocate, GA_SPOTLIGHT);
}

/**
 * Gets a VALUE for a betslip section, e.g. betslip_whatever
 * @param {String} betLocate param
 * @returns {String} the attribute value
 */
export function getGaAttributeValueBetslip(betLocate) {
    return `${GA_PREFIX_BETSLIP}_${betLocate}`;
}

/**
 * Gets a VALUE for a homepage Popular Bet Builder, e.g. checkdmedia_homepage_tab-1_iframe_bb
 * @param {String} locateBase the base location string
 * @returns {String} GA attribute value
 */
export function getGaAttributeValueHomepagePopularBetBuilder(locateBase) {
    const savedTabKey = getHomepageGaTabKey();

    return locateBase.replace('<location>', `homepage_${savedTabKey}`);
}
